import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        // Runs on its own so the add button frees up straight away.
        this.#afterAdd(line);
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  // After a successful add: the product photo flies into the cart icon. If this was the first item in an
  // empty cart, the case offer opens 2 seconds after the add and the visitor stays on the page.
  // Otherwise the cart opens as usual once the photo lands.
  async #afterAdd(line) {
    const start = performance.now();
    const offer = document.querySelector('s-case-offer');
    const flight = this.#flyToCart();
    const eligible = await offer?.isEligible?.(line.merchandiseId, line.quantity);
    await flight;

    if (!eligible) {
      await window.Shopify.actions.openCart?.();
      return;
    }
    const wait = Math.max(0, 2000 - (performance.now() - start));
    await new Promise((resolve) => window.setTimeout(resolve, wait));
    const outcome = await offer.show();
    // Another overlay was already up (or the visitor opened the cart meanwhile): fall back to the cart.
    if (!outcome && !document.querySelector('dialog:modal')) await window.Shopify.actions.openCart?.();
  }

  #flyToCart() {
    const bump = () => document.dispatchEvent(new CustomEvent('mab:cart-bump'));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const target = document.getElementById('header-cart-trigger');
    const source = this.hasAttribute('data-product-form')
      ? [...document.querySelectorAll('[data-product-gallery] img')].find((img) => img.getBoundingClientRect().width > 0)
      : null;
    if (reduce || !target || !source || !source.animate) {
      bump();
      return Promise.resolve();
    }

    const from = source.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const size = Math.min(from.width, from.height, 220);
    const ghost = source.cloneNode(false);
    ghost.removeAttribute('id');
    ghost.removeAttribute('loading');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.className = '';
    Object.assign(ghost.style, {
      position: 'fixed',
      zIndex: '2147483646',
      pointerEvents: 'none',
      left: `${from.left + (from.width - size) / 2}px`,
      top: `${from.top + (from.height - size) / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      objectFit: 'cover',
      borderStartEndRadius: '1.125rem',
      borderEndStartRadius: '1.125rem',
      boxShadow: '0 12px 32px rgb(20 18 15 / 0.25)',
    });
    document.body.appendChild(ghost);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const animation = ghost.animate([
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 60}px) scale(0.55)`, opacity: 1, offset: 0.5 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.08)`, opacity: 0.4 },
    ], { duration: 750, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' });

    return animation.finished.catch(() => {}).then(() => {
      ghost.remove();
      bump();
    });
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}

// Cart icon bounce, triggered after anything lands in the cart.
document.addEventListener('mab:cart-bump', () => {
  const icon = document.getElementById('header-cart-trigger');
  if (!icon) return;
  icon.classList.remove('cart-bump');
  void icon.offsetWidth;
  icon.classList.add('cart-bump');
  icon.addEventListener('animationend', () => icon.classList.remove('cart-bump'), { once: true });
});
